// File: frontend/src/components/notifications/WelcomeRuleModal.tsx
// src/components/notifications/WelcomeRuleModal.tsx

import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { FaTimes } from "react-icons/fa";
import { statisticsService } from "../../services/statisticsService";
import "./WelcomeRuleModal.css";

interface WelcomeRuleModalProps {
  // Required entry count for the rookie approval ranking — comes from the
  // WELCOME notification's targetLabel (see NotificationBell.buildSnippet)
  requiredEntries: number;
  onClose: () => void;
}

/**
 * Explains the rookie approval ranking rule ("Çaylak Onay Sırası") in a
 * centered modal, opened by clicking the WELCOME notification in the bell
 * dropdown. Previously that click did nothing but mark the notification as
 * read, so the rule text in the dropdown snippet was easy to miss/dismiss.
 *
 * Fetches the user's current entry count (same endpoint as UserStatsModal)
 * so the progress bar reflects real progress instead of just repeating the
 * static rule sentence.
 */
export const WelcomeRuleModal: React.FC<WelcomeRuleModalProps> = ({
  requiredEntries,
  onClose,
}) => {
  const { t } = useTranslation();

  const [currentEntries, setCurrentEntries] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  // Fetch the live entry count once, on open
  useEffect(() => {
    let cancelled = false;
    statisticsService
      .getUserRanking()
      .then((result) => {
        if (!cancelled) setCurrentEntries(result.entryCount);
      })
      .catch(() => {
        // Progress bar is skipped below when currentEntries stays null
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  const completed = currentEntries !== null && currentEntries >= requiredEntries;
  const progressPct =
    currentEntries !== null
      ? Math.min(100, Math.round((currentEntries / requiredEntries) * 100))
      : 0;

  return ReactDOM.createPortal(
    <div
      className="welcome-rule-modal-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="welcome-rule-modal" role="dialog" aria-modal="true">
        <button
          className="welcome-rule-modal__close-btn"
          onClick={onClose}
          aria-label={t("close")}
        >
          <FaTimes />
        </button>

        <div className="welcome-rule-modal__icon">👋</div>

        <h3 className="welcome-rule-modal__title">{t("notif_welcome_title")}</h3>

        <p className="welcome-rule-modal__body">
          {t("notif_welcome_body", { count: requiredEntries })}
        </p>

        {!loading && currentEntries !== null && (
          <div className="welcome-rule-modal__progress">
            <div className="welcome-rule-modal__progress-label">
              {completed
                ? t("welcome_modal_progress_done")
                : t("welcome_modal_progress_label", {
                    current: currentEntries,
                    required: requiredEntries,
                  })}
            </div>
            <div className="welcome-rule-modal__progress-track">
              <div
                className={`welcome-rule-modal__progress-fill${
                  completed ? " welcome-rule-modal__progress-fill--done" : ""
                }`}
                style={{ width: `${progressPct}%` }}
              />
            </div>
          </div>
        )}

        <button className="welcome-rule-modal__got-it-btn" onClick={onClose} autoFocus>
          {t("welcome_modal_got_it")}
        </button>
      </div>
    </div>,
    document.body
  );
};